import { useMemo } from "react"

import { projectAtoms } from "#atoms"
import GenericFormModal from "#blocks/Form/GenericFormModal"
import Firebase, { NULL_ID } from "#classes/Firebase"
import { ANALYTICS_EVENTS } from "#classes/FirebaseAnalytics"
import { CLASS_GROUPS } from "#constants/classOptions"
import { RESOURCES } from "#customTypes/composedTypes/authTypes"
import { CurriculumConstructor } from "#customTypes/composedTypes/curriculumTypes"
import {
  COMPARATORS,
  DOC_TYPES,
} from "#customTypes/composedTypes/firebaseTypes"
import {
  type AddProjectFormSubmitValues,
  type AddProjectFormValues,
  ProjectConstructor,
} from "#customTypes/composedTypes/projectTypes"
import { StudentConstructor } from "#customTypes/composedTypes/studentTypes"
import { Uuid } from "#customTypes/globalTypes"
import { useOneDoc } from "#hooks/jotai/useSubscription"
import { useResourcePermissions } from "#hooks/newHooks/useHasPermission"
import { useFirebaseTransaction } from "#hooks/reactQuery/useFirebaseMutation"
import useAnalytics from "#hooks/useAnalytics"

import AddProjectForm from "./AddProjectForm"

export interface AddProjectFormModalProps {
  onClose: () => void
  projectId: Uuid | null
}

const AddProjectFormModal = ({
  onClose,
  projectId,
}: AddProjectFormModalProps) => {
  const isEdit = projectId != null
  const { trackEvent, trackProjectAction } = useAnalytics()

  const { canCreate, canRead, canUpdate, canDelete } = useResourcePermissions(
    RESOURCES.PROJECT
  )

  const projectFromDb = useOneDoc<ProjectConstructor>(
    projectAtoms,
    DOC_TYPES.PROJECTS,
    (canRead && projectId) || NULL_ID
  )
  const initialValues = useMemo(() => {
    if (!projectFromDb || !canRead) return { classGroup: CLASS_GROUPS.CORE }
    return projectFromDb
  }, [projectFromDb, canRead])
  // `{ classGroup }` is the create-mode stub. A saved project always has `id`.
  const savedProject = "id" in initialValues ? initialValues : undefined

  // Errors are handled in GenericFormModal
  const handleFinish = async (
    cleanedValues: AddProjectFormValues
  ): Promise<void> => {
    if (!canCreate && !isEdit) {
      throw new Error("You do not have permission to create projects")
    }
    if (!canUpdate && isEdit) {
      throw new Error("You do not have permission to update projects")
    }

    const data: AddProjectFormValues | AddProjectFormSubmitValues =
      cleanedValues

    if (savedProject != null) {
      data.id = savedProject.id
    }

    const projectId = await Firebase.getInstance().addDocument(
      DOC_TYPES.PROJECTS,
      data
    )

    trackProjectAction(
      isEdit
        ? ANALYTICS_EVENTS.PROJECT_UPDATED
        : ANALYTICS_EVENTS.PROJECT_CREATED,
      {
        projectId,
        projectName: data.name,
      }
    )
  }

  const { mutate: handleTransaction } = useFirebaseTransaction()

  const handleDelete = async () => {
    if (!canDelete) {
      throw new Error("You do not have permission to delete projects")
    }

    if (savedProject == null) return

    const curriculumsPromise =
      Firebase.getInstance().variableQuery<CurriculumConstructor>(
        DOC_TYPES.CURRICULUMS,
        {
          queries: {
            projectIds: {
              value: savedProject.id,
              comparator: COMPARATORS.CONTAINS,
            },
          },
        }
      )

    // Check student project completion
    const studentsPromise =
      Firebase.getInstance().variableQuery<StudentConstructor>(
        DOC_TYPES.STUDENTS,
        {
          queries: {
            [`projects.${savedProject.id}`]: {
              value: null,
              comparator: COMPARATORS.DOES_NOT_EQUAL,
            },
          },
        }
      )

    const [curriculums, students] = await Promise.all([
      curriculumsPromise,
      studentsPromise,
    ])

    const errors: string[] = []

    if (curriculums.length > 0) {
      const message = `Please remove this project from the following curriculums before deleting it: 
      
${curriculums.map((c) => c.name).join("\n")}`
      errors.push(message)
    }

    if (students.length > 0) {
      const message = `Please remove this project from the following students before deleting it: 
      
${students.map((s) => s.name).join("\n")}`
      errors.push(message)
    }

    if (errors.length > 0) {
      throw new Error(errors.join("\n\n"))
    }

    await handleTransaction([
      {
        docType: DOC_TYPES.PROJECTS,
        id: savedProject.id,
        data: null,
        action: "delete",
      },
    ])

    // `trackProjectAction` only accepts created/updated. `PROJECT_DELETED`
    // exists on ANALYTICS_EVENTS and is accepted by `trackEvent`.
    trackEvent(ANALYTICS_EVENTS.PROJECT_DELETED, {
      projectId: savedProject.id,
      projectName: savedProject.name,
    })

    // Brief delay to allow Firebase listeners to process the deletion
    setTimeout(() => {
      onClose()
    }, 500)
  }

  return (
    <GenericFormModal
      title={
        isEdit && savedProject != null
          ? `Project: ${savedProject.name}`
          : "Add Project"
      }
      onClose={onClose}
      initialValues={initialValues}
      onFinish={handleFinish}
      resource={RESOURCES.PROJECT}
      onDelete={handleDelete}
    >
      <AddProjectForm initialValues={initialValues} />
    </GenericFormModal>
  )
}

export default AddProjectFormModal
